/**
 * Presentation helpers for the contractor-facing BondReady request portfolio.
 * Classification and labels only � does not invent persisted lifecycle state.
 */

import {
  customerFacingPartnerDisplayName,
  humanDate,
  nextActionPresentation,
  ownerPresentation,
  requestStageLabel,
  workflowLabel,
} from '../bondready/bondreadyPresentation';

export const CONTRACTOR_BUCKET = {
  NEEDS_ATTENTION: 'needs_attention',
  WAITING_ON_PARTNER: 'waiting_on_partner',
  COMPLETED: 'completed',
};

/** Project / request title for contractor rows. Prefers scenario project slug. */
export function contractorProjectLabel(opportunity) {
  const persisted = typeof opportunity?.projectName === 'string' ? opportunity.projectName.trim() : '';
  if (persisted) return persisted;
  const src = typeof opportunity?.sourceRecordId === 'string' ? opportunity.sourceRecordId : '';
  const match = src.match(/^scenario-bond-request:[^:]+:([^:]+)/i);
  if (match) {
    return match[1]
      .split(/[-_]+/)
      .filter(Boolean)
      .map((part, index) => (
        index === 0 && part.length <= 3
          ? part.toUpperCase()
          : part.charAt(0).toUpperCase() + part.slice(1)
      ))
      .join(' ');
  }
  const details = typeof opportunity?.bondDetails === 'string' ? opportunity.bondDetails.trim() : '';
  if (details && !/^customer requests assistance/i.test(details)) return details;
  const subject = typeof opportunity?.subjectName === 'string' ? opportunity.subjectName.trim() : '';
  return subject || 'Bond request';
}

export function contractorRequestTypeLabel(requestType) {
  if (requestType === 'BOND') return 'Bond request';
  if (requestType === 'FUNDING') return 'Working capital request';
  return 'Request';
}

/** Human status from canonical request stage / workflow � no SCREAMING_SNAKE dump. */
export function contractorRequestStatusLabel({ requestStage, requestStatus, workflowStatus } = {}) {
  const stage = typeof requestStage === 'string' ? requestStage.trim() : '';
  if (stage === 'INFORMATION_NEEDED') return 'Information needed';
  if (stage === 'RESPONSE_PROVIDED') return 'Response sent';
  if (stage === 'SUBMITTED') return 'Submitted';
  if (stage === 'INTAKE') return 'Intake';
  if (stage === 'DOCUMENT_COLLECTION') return 'Document collection';
  if (requestStatus === 'CLOSED' || workflowStatus === 'CLOSED') return 'Closed';
  if (requestStatus === 'DECLINED' || workflowStatus === 'DECLINED') return 'Declined';
  if (stage) {
    const labeled = requestStageLabel(stage);
    if (labeled && labeled !== 'Unknown / not supplied') return labeled;
  }
  if (workflowStatus === 'NOT_REVIEWED' && stage === '') return 'Submitted';
  return workflowLabel(workflowStatus);
}

/**
 * True when the authenticated contractor organization currently owns the next action.
 * Uses the same owner strings already written by BondReady services.
 */
export function contractorOwnsNextAction(currentOwner, orgDisplayName) {
  const owner = typeof currentOwner === 'string' ? currentOwner.trim().toLowerCase() : '';
  const org = typeof orgDisplayName === 'string' ? orgDisplayName.trim().toLowerCase() : '';
  if (!owner) return false;
  if (/holmes\s*murphy/i.test(owner)) return false;
  if (/^constructpay(\s+bondready)?$/i.test(owner.trim())) return false;
  if (org && owner.includes(org)) return true;
  // First significant token of org name (e.g. "Sayrick" from "Sayrick Construction Group")
  const token = org.split(/\s+/).find((part) => part.length > 2);
  if (token && owner.includes(token)) return true;
  return false;
}

export function outstandingInformationRequestCount(informationRequests) {
  if (!Array.isArray(informationRequests)) return 0;
  return informationRequests.reduce((count, infoRequest) => {
    if (!infoRequest || infoRequest.status === 'RESOLVED') return count;
    const items = Array.isArray(infoRequest.items) ? infoRequest.items : [];
    const pending = items.filter((item) => item?.responseState === 'PENDING').length;
    if (pending > 0) return count + 1;
    if (infoRequest.status === 'OPEN' || infoRequest.status === 'RESPONSE_PROVIDED') {
      return count + (items.length ? 0 : 1);
    }
    return count;
  }, 0);
}

export function classifyContractorRequestBucket({
  workflowStatus,
  requestStatus,
  requestStage,
  currentOwner,
  orgDisplayName,
  outstandingInfoCount = 0,
} = {}) {
  if (
    workflowStatus === 'CLOSED'
    || workflowStatus === 'DECLINED'
    || requestStatus === 'CLOSED'
    || requestStatus === 'DECLINED'
  ) {
    return CONTRACTOR_BUCKET.COMPLETED;
  }

  const owns = contractorOwnsNextAction(currentOwner, orgDisplayName)
    || requestStage === 'INFORMATION_NEEDED'
    || Number(outstandingInfoCount) > 0;

  if (owns) return CONTRACTOR_BUCKET.NEEDS_ATTENTION;
  return CONTRACTOR_BUCKET.WAITING_ON_PARTNER;
}

export function contractorYourActionCopy({
  nextAction,
  outstandingInfoCount = 0,
  bucket,
} = {}) {
  const presented = nextActionPresentation(nextAction);
  if (bucket === CONTRACTOR_BUCKET.NEEDS_ATTENTION && Number(outstandingInfoCount) > 0) {
    const n = Number(outstandingInfoCount);
    return `Your action: Review and respond to ${n} information request${n === 1 ? '' : 's'}`;
  }
  if (bucket === CONTRACTOR_BUCKET.NEEDS_ATTENTION && presented) {
    return `Your action: ${presented}`;
  }
  if (bucket === CONTRACTOR_BUCKET.WAITING_ON_PARTNER) {
    if (/response sent|response_provided|RESPONSE_PROVIDED/i.test(String(nextAction || ''))) {
      return 'Response sent \u2014 awaiting partner review';
    }
    return presented ? `Awaiting partner: ${presented}` : 'Submitted \u2014 awaiting partner review';
  }
  return presented || null;
}

export function contractorUpdatedLabel(updatedAt) {
  if (updatedAt == null || updatedAt === '') return null;
  return `Updated ${humanDate(updatedAt)}`;
}

export function opportunityDetailPath(opportunityId) {
  if (opportunityId == null || opportunityId === '') return null;
  return `/bondready/${String(opportunityId)}`;
}

/**
 * Build one portfolio row from canonical opportunity + request + optional info requests.
 * Does not invent owners/actions � uses authoritative fields already preferred by the tracker.
 */
export function buildContractorPortfolioRow({
  opportunity,
  request,
  informationRequests = [],
  partnerDisplayName = null,
  orgDisplayName = null,
} = {}) {
  if (!opportunity || !request) return null;
  const ownerSource = request.currentOwner || opportunity.currentOwner;
  const nextSource = request.nextAction || opportunity.nextAction;
  const outstandingInfoCount = outstandingInformationRequestCount(informationRequests);
  const bucket = classifyContractorRequestBucket({
    workflowStatus: opportunity.workflowStatus,
    requestStatus: request.status,
    requestStage: request.stage,
    currentOwner: ownerSource,
    orgDisplayName,
    outstandingInfoCount,
  });
  const projectLabel = contractorProjectLabel({
    ...opportunity,
    projectName: request.projectContext?.projectName || null,
    bondDetails: request.details || opportunity.bondDetails,
  });
  return {
    id: `${opportunity.id}:${request.id}`,
    opportunityId: opportunity.id,
    requestId: request.id,
    owningOrganizationId: opportunity.owningOrganizationId,
    viewerRole: opportunity.viewerRole,
    projectLabel,
    requestTypeLabel: contractorRequestTypeLabel(request.requestType),
    partnerLabel: customerFacingPartnerDisplayName(partnerDisplayName),
    statusLabel: contractorRequestStatusLabel({
      requestStage: request.stage,
      requestStatus: request.status,
      workflowStatus: opportunity.workflowStatus,
    }),
    currentOwner: ownerPresentation(ownerSource),
    nextAction: nextActionPresentation(nextSource),
    outstandingInfoCount,
    yourActionCopy: contractorYourActionCopy({
      nextAction: nextSource,
      outstandingInfoCount,
      bucket,
    }),
    updatedLabel: contractorUpdatedLabel(request.updatedAt || opportunity.updatedAt),
    bucket,
    detailPath: opportunityDetailPath(opportunity.id),
  };
}

export function partitionContractorPortfolioRows(rows) {
  const needsAttention = [];
  const waitingOnPartner = [];
  const completed = [];
  (rows || []).forEach((row) => {
    if (!row) return;
    if (row.bucket === CONTRACTOR_BUCKET.NEEDS_ATTENTION) needsAttention.push(row);
    else if (row.bucket === CONTRACTOR_BUCKET.COMPLETED) completed.push(row);
    else waitingOnPartner.push(row);
  });
  return { needsAttention, waitingOnPartner, completed };
}
